import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { designsAPI } from '@/services/api';
import { toast } from 'sonner';
import { Wand2, Sparkles, Loader2, ImageIcon, FolderOpen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Helmet } from 'react-helmet';
import AuthModal from '@/components/Auth/AuthModal';
import ProjectDialog from '@/components/ProjectDialog';
import { db } from '@/config/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';

const Generate = () => {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('ASPECT_1_1');
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [processingProjects, setProcessingProjects] = useState([]);
  const [pendingGeneration, setPendingGeneration] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [regeneratingProjects, setRegeneratingProjects] = useState(new Map());

  const aspectRatios = [
    { value: 'ASPECT_1_1', label: 'Square (1:1)' },
    { value: 'ASPECT_16_9', label: 'Wide (16:9)' },
    { value: 'ASPECT_9_16', label: 'Tall (9:16)' },
    { value: 'ASPECT_4_3', label: 'Classic (4:3)' },
    { value: 'ASPECT_3_4', label: 'Portrait (3:4)' },
    { value: 'ASPECT_4_5', label: 'Portrait (4:5)' },
    { value: 'ASPECT_5_4', label: 'Landscape (5:4)' },
  ];

  // Listen to processing_projects for active generations
  useEffect(() => {
    if (!currentUser) {
      setProcessingProjects([]);
      setRegeneratingProjects(new Map());
      return;
    }

    const processingRef = collection(db, 'users', currentUser.uid, 'processing_projects');
    
    const unsubscribe = onSnapshot(processingRef, (snapshot) => {
      const processingList = [];
      const regenMap = new Map();
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        const docId = doc.id;
        
        // Calculate seconds remaining based on createdAt timestamp
        const createdAtMs = data.createdAt?.toMillis() || Date.now();
        const elapsedSeconds = Math.floor((Date.now() - createdAtMs) / 1000);
        const secondsRemaining = Math.max(0, 30 - elapsedSeconds); // 30 seconds countdown
        
        // Check if this is a regeneration (starts with "regen_")
        if (docId.startsWith('regen_')) {
          // Extract projectId from regeneration ID: regen_${projectId}_${timestamp}
          const parts = docId.split('_');
          if (parts.length >= 3) {
            const projectId = parts.slice(1, -1).join('_'); // Handle projectIds that might contain underscores
            regenMap.set(projectId, {
              id: docId,
              projectId,
              prompt: data.prompt,
              aspectRatio: data.aspectRatio,
              createdAt: createdAtMs,
              secondsRemaining,
            });
          }
        } else {
          // Regular generation
          processingList.push({
            id: doc.id,
            projectId: data.projectId,
            prompt: data.prompt,
            aspectRatio: data.aspectRatio,
            type: data.type,
            createdAt: createdAtMs,
            secondsRemaining,
          });
        }
      });
      
      setProcessingProjects(processingList);
      setRegeneratingProjects(regenMap);
      
      // Check if our pending generation has appeared
      if (pendingGeneration && processingList.some(p => p.prompt === pendingGeneration)) {
        console.log('✅ Processing project detected in Firestore - stopping button loading');
        setIsButtonLoading(false);
        setPendingGeneration(null);
        toast.success('Design generation started!');
      }
    }, (error) => {
      console.error('Error fetching processing projects:', error);
    });

    return () => unsubscribe();
  }, [currentUser, pendingGeneration]);

  // Update countdown for processing projects
  useEffect(() => {
    if (processingProjects.length === 0 && regeneratingProjects.size === 0) return;

    const interval = setInterval(() => {
      // Update regular processing projects
      setProcessingProjects(prevProjects => {
        return prevProjects.map(proj => {
          const elapsedSeconds = Math.floor((Date.now() - proj.createdAt) / 1000);
          const secondsRemaining = Math.max(0, 30 - elapsedSeconds); // 30 seconds countdown
          return { ...proj, secondsRemaining };
        }).filter(proj => proj.secondsRemaining > 0); // Remove if countdown complete
      });

      // Update regenerating projects
      setRegeneratingProjects(prevMap => {
        const newMap = new Map();
        prevMap.forEach((regen, projectId) => {
          const elapsedSeconds = Math.floor((Date.now() - regen.createdAt) / 1000);
          const secondsRemaining = Math.max(0, 30 - elapsedSeconds);
          
          if (secondsRemaining > 0) {
            newMap.set(projectId, { ...regen, secondsRemaining });
          }
        });
        return newMap;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [processingProjects.length, regeneratingProjects.size]);

  // Handle auto-generate from landing page
  useEffect(() => {
    if (location.state?.autoGenerate && location.state?.prompt) {
      setPrompt(location.state.prompt);
      setAspectRatio(location.state.aspectRatio || 'ASPECT_1_1');
      
      // Clear the location state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      
      // Trigger generation after a brief delay to ensure state is set
      setTimeout(() => {
        handleGenerate(location.state.prompt, location.state.aspectRatio || 'ASPECT_1_1');
      }, 100);
    }
  }, [location.state]);

  // Load projects from Firestore
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setProjects([]);
      setHasLoadedOnce(false);
      return;
    }

    // Only show loading shimmer if we haven't loaded data before
    if (!hasLoadedOnce) {
      setLoading(true);
    }

    const projectsRef = collection(db, 'users', currentUser.uid, 'projects');
    const q = query(projectsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projectsList = [];
      snapshot.forEach((doc) => {
        projectsList.push({ id: doc.id, ...doc.data() });
      });
      setProjects(projectsList);
      
      // Update selectedProject if it's currently open and has been updated
      if (selectedProject) {
        const updatedProject = projectsList.find(
          p => p.projectId === selectedProject.projectId
        );
        if (updatedProject) {
          setSelectedProject(updatedProject);
        }
      }
      
      // Only add delay on first load, otherwise set immediately
      if (!hasLoadedOnce) {
        setTimeout(() => {
          setLoading(false);
          setHasLoadedOnce(true);
        }, 500);
      } else {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
      setLoading(false);
      setHasLoadedOnce(true);
    });

    return () => unsubscribe();
  }, [currentUser, selectedProject?.projectId]);

  const handleGenerate = async (overridePrompt = null, overrideAspectRatio = null) => {
    const finalPrompt = overridePrompt || prompt;
    const finalAspectRatio = overrideAspectRatio || aspectRatio;

    if (!finalPrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Check if user is logged in
    if (!currentUser) {
      setAuthModalOpen(true);
      return;
    }

    if ((userProfile?.credits || 0) < 5) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    console.log('🚀 Starting generation...');
    setIsButtonLoading(true);
    setPendingGeneration(finalPrompt);
    
    // Clear the prompt immediately
    setPrompt('');

    // Safety timeout in case Firestore listener fails
    const safetyTimeout = setTimeout(() => {
      if (pendingGeneration) {
        console.log('⚠️ Safety timeout - forcing button stop');
        setIsButtonLoading(false);
        setPendingGeneration(null);
        toast.success('Design generation started!');
      }
    }, 10000); // 10 second safety net

    // Fire the API call
    designsAPI.generate(
      finalPrompt,
      null,
      finalAspectRatio
    ).then(() => {
      console.log('✅ Generation API completed');
      clearTimeout(safetyTimeout);
      
      // Refresh profile in background
      if (refreshProfile) {
        refreshProfile().catch(err => console.error('Failed to refresh profile:', err));
      }
    }).catch((error) => {
      console.error('❌ Generation error:', error);
      clearTimeout(safetyTimeout);
      setIsButtonLoading(false);
      setPendingGeneration(null);
      toast.error(error.message || 'Failed to generate design');
    });
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setProjectDialogOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Generate Design - Fluxara</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-turquoise-50 to-blue-50 gradient-mesh">
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Hero Section with Split Layout */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h1 className="text-5xl font-black mb-4 bg-gradient-to-r from-cyan-600 to-turquoise-600 bg-clip-text text-transparent">
                Create Your Design
              </h1>
              <p className="text-xl text-gray-600">Transform your ideas into stunning printable designs with AI</p>
            </div>

            {/* Generation Card - Compact Single Line */}
            <div className="max-w-6xl mx-auto">
              {/* Single Line Input with Options Inside */}
              <div className="flex flex-col md:flex-row md:items-end gap-3 bg-white rounded-3xl shadow-2xl border border-gray-200 p-2 pr-3">
                {/* Text Input - Auto-expanding Textarea */}
                <Textarea
                  placeholder="e.g., A majestic dragon in cyberpunk style..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={isButtonLoading}
                  data-testid="prompt-input"
                  rows={1}
                  className="flex-1 px-4 py-3 text-base leading-6 outline-none bg-transparent border-0 shadow-none focus-visible:ring-0 resize-none min-h-[44px] !max-h-[280px] md:!max-h-[120px] overflow-y-auto"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                  style={{
                    height: 'auto',
                  }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    const maxHeight = window.innerWidth >= 768 ? 120 : 280;
                    e.target.style.height = Math.min(e.target.scrollHeight, maxHeight) + 'px';
                  }}
                />
                
                {/* Controls - Responsive: Inside on medium+, Outside full-width on mobile only */}
                <div className="flex items-center gap-3 w-full md:w-auto self-end">
                  {/* Aspect Ratio - Inside Input */}
                  <Select value={aspectRatio} onValueChange={setAspectRatio} disabled={isButtonLoading}>
                    <SelectTrigger data-testid="aspect-ratio-select" className="flex-[2] md:flex-none md:w-32 h-11 border-0 bg-gray-100 hover:bg-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aspectRatios.map((ratio) => (
                        <SelectItem key={ratio.value} value={ratio.value}>
                          {ratio.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Generate Button */}
                  <Button
                    className="flex-[3] md:flex-none bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-300 hover:to-cyan-300 text-gray-900 font-bold px-6 h-11 rounded-full transition-all duration-300 shadow-lg"
                    onClick={() => handleGenerate()}
                    disabled={isButtonLoading}
                    data-testid="generate-button"
                  >
                    {isButtonLoading ? (
                      <>
                        <Loader2 className="animate-spin mr-2 w-4 h-4" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 w-4 h-4" />
                        Generate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Processing Projects - Multiple Progress Cards */}
          {processingProjects.length > 0 && (
            <div className="space-y-4 mb-12">
              {processingProjects.map((processing) => {
                const timeDisplay = `${processing.secondsRemaining}s`;
                
                return (
                  <Card 
                    key={processing.id} 
                    className="border-2 border-cyan-300 bg-gradient-to-r from-cyan-50 to-turquoise-50 shadow-xl"
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-2xl">
                        <Loader2 className="animate-spin w-7 h-7 text-cyan-600" />
                        Generation in Progress
                      </CardTitle>
                      <CardDescription className="text-base font-medium text-gray-700">
                        "{processing.prompt}"
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm font-medium text-cyan-900">
                          <span>✨ Creating your masterpiece...</span>
                          <span>{timeDisplay} remaining</span>
                        </div>
                        <Progress 
                          value={((30 - processing.secondsRemaining) / 30) * 100} 
                          className="h-3" 
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Projects Grid */}
          <div className="pt-8">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-4xl font-black bg-gradient-to-r from-cyan-600 to-turquoise-600 bg-clip-text text-transparent flex items-center gap-3 pb-4">
                <FolderOpen className="w-8 h-8 text-cyan-600" />
                My Projects
              </h2>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {[...Array(8)].map((_, index) => (
                  <Card key={index} className="glass border-0 overflow-hidden">
                    <CardHeader className="p-0 relative">
                      {/* Shimmer Image Placeholder */}
                      <div className="aspect-square bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer bg-[length:200%_100%]"></div>
                    </CardHeader>
                    <CardContent className="p-4 bg-white/80">
                      {/* Shimmer Title */}
                      <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer bg-[length:200%_100%] rounded mb-2"></div>
                      {/* Shimmer Text */}
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer bg-[length:200%_100%] rounded"></div>
                        <div className="h-4 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer bg-[length:200%_100%] rounded"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : !currentUser ? (
              <Card className="glass shadow-lg border-0">
                <CardContent className="p-12 text-center">
                  <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Sign in to save your projects</h3>
                  <p className="text-gray-600 mb-6">Create an account to save and manage your designs</p>
                  <Button
                    onClick={() => setAuthModalOpen(true)}
                    className="bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700 shadow-lg"
                  >
                    Sign In / Sign Up
                  </Button>
                </CardContent>
              </Card>
            ) : projects.length === 0 ? (
              <Card className="glass shadow-2xl border-0">
                <CardContent className="p-16 text-center">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-cyan-400 rounded-full blur-2xl opacity-30 animate-pulse" />
                    <FolderOpen className="relative w-24 h-24 text-cyan-400" />
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-gray-900">No projects yet</h3>
                  <p className="text-gray-600 text-lg mb-8">Generate your first design above to get started!</p>
                  <div className="flex gap-4 justify-center text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      <span>AI-Powered</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4" />
                      <span>Easy to Use</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {projects.map((project) => {
                  const mainVersion = project.versions?.find(v => v.versionId === project.mainVersionId) 
                    || project.versions?.[project.versions.length - 1];
                  const editCount = project.versions?.length || 0;
                  const maxEdits = 10;
                  const isRegenerating = regeneratingProjects.has(project.projectId);
                  const regenProgress = regeneratingProjects.get(project.projectId);
                  
                  return (
                    <Card 
                      key={project.id} 
                      className="group glass hover:shadow-2xl transition-all duration-500 cursor-pointer border-0 overflow-hidden hover:scale-105 hover:-translate-y-2"
                      onClick={() => openProject(project)}
                      data-testid={`project-${project.id}`}
                    >
                      <CardHeader className="p-0 relative">
                        <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden relative">
                          {mainVersion?.finalImageUrl ? (
                            <>
                              <img
                                src={mainVersion.finalImageUrl}
                                alt={project.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                              {/* Regenerating Overlay */}
                              {isRegenerating && regenProgress && (
                                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center p-4 text-white">
                                  <Loader2 className="animate-spin w-8 h-8 mb-3" />
                                  <p className="text-sm font-bold mb-2">Regenerating...</p>
                                  <p className="text-xs text-center mb-3 italic line-clamp-2">"{regenProgress.prompt}"</p>
                                  <div className="w-full">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span>Progress</span>
                                      <span>{regenProgress.secondsRemaining}s</span>
                                    </div>
                                    <Progress 
                                      value={((30 - regenProgress.secondsRemaining) / 30) * 100} 
                                      className="h-2 bg-gray-700" 
                                    />
                                  </div>
                                </div>
                              )}
                              {/* Normal Hover Overlay - Only show when NOT regenerating */}
                              {!isRegenerating && (
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                                  <span className="text-white font-bold text-lg">Open Project →</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FolderOpen className="w-16 h-16 text-gray-300" />
                            </div>
                          )}
                          {/* Version Badge */}
                          <div className="absolute top-3 right-3">
                            <Badge variant={editCount < maxEdits ? "default" : "destructive"} className="text-xs font-bold shadow-lg bg-white/90 text-gray-900">
                              {editCount}/{maxEdits}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 bg-white/80">
                        <CardTitle className="truncate text-lg mb-2 font-bold text-gray-900">{project.name}</CardTitle>
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <span className="font-medium">{editCount} version{editCount !== 1 ? 's' : ''}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        open={authModalOpen} 
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          setAuthModalOpen(false);
          toast.success('Successfully logged in! You can now generate designs.');
        }}
      />

      {/* Project Dialog */}
      {selectedProject && (
        <ProjectDialog
          open={projectDialogOpen}
          onClose={() => {
            setProjectDialogOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onUpdate={() => {
            // Projects will auto-update via Firestore listener
          }}
          onDelete={() => {
            setProjectDialogOpen(false);
            setSelectedProject(null);
          }}
        />
      )}
    </>
  );
};

export default Generate;
